import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getJob, matchCandidates } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Briefcase, MapPin, Users, Calendar, Search,
  CheckCircle, XCircle, Clock, Sparkles
} from 'lucide-react';
import toast from 'react-hot-toast';

const canMatch = ['super_admin', 'branch_manager', 'recruiter', 'finance'];

const statusColor = (s) =>
  s === 'open'        ? 'bg-green-50 text-green-700' :
  s === 'in_progress' ? 'bg-blue-50 text-blue-700' :
  s === 'fulfilled'   ? 'bg-gray-50 text-gray-500' :
  s === 'cancelled'   ? 'bg-red-50 text-red-600' :
  'bg-amber-50 text-amber-700';

const priorityColor = (p) =>
  p === 'urgent' ? 'bg-red-50 text-red-600' :
  p === 'high'   ? 'bg-orange-50 text-orange-600' :
  p === 'normal' ? 'bg-blue-50 text-blue-600' :
  'bg-gray-50 text-gray-500';

const stageColor = (s) =>
  s === 'selected'  ? 'bg-green-50 text-green-700' :
  s === 'rejected'  ? 'bg-red-50 text-red-600' :
  s === 'offer'     ? 'bg-purple-50 text-purple-700' :
  s === 'interview' ? 'bg-blue-50 text-blue-700' :
  'bg-amber-50 text-amber-700';

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between py-2 border-b border-gray-50">
    <span className="text-sm text-gray-400">{label}</span>
    <span className="text-sm font-medium text-gray-800">{value || '—'}</span>
  </div>
);

const JobDetail = () => {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const { user }   = useAuth();
  const [tab, setTab] = useState('overview');

  const { data: job, isLoading } = useQuery({
    queryKey: ['job', id],
    queryFn : () => getJob(id).then(r => r.data.data)
  });

  const matchMutation = useMutation({
    mutationFn: () => matchCandidates(id),
    onSuccess : (res) => {
      toast.success(`${res.data.data.length} matching candidates found`);
      setTab('matches');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to find matches')
  });

  if (isLoading) return (
    <div className="flex items-center justify-center h-64 text-gray-400">
      Loading job requisition...
    </div>
  );

  if (!job) return (
    <div className="text-center py-12 text-gray-400">Job requisition not found</div>
  );

  const tabs = [
    { id: 'overview',     label: 'Overview' },
    { id: 'applications', label: `Applications (${job._count?.applications || 0})` },
    { id: 'matches',      label: 'Smart Matches' }
  ];

  return (
    <div className="space-y-5">
      {/* Back Button */}
      <button onClick={() => navigate('/jobs')}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft size={16} />
        Back to Jobs
      </button>

      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-start gap-5">
          <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center flex-shrink-0">
            <Briefcase size={26} className="text-amber-600" />
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl font-semibold text-gray-900">{job.title}</h1>
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColor(job.status)}`}>
                {job.status?.replace('_', ' ')}
              </span>
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${priorityColor(job.priority)}`}>
                {job.priority}
              </span>
            </div>

            <p className="text-sm text-gray-400 mt-1">
              {job.requisition_no} · {job.clients?.company_name}
            </p>

            <div className="flex flex-wrap gap-4 mt-3">
              {job.location && (
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <MapPin size={14} className="text-gray-400" />
                  {job.location}
                </div>
              )}
              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                <Users size={14} className="text-gray-400" />
                {job.filled_positions}/{job.positions} filled
              </div>
              {job.start_date && (
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <Calendar size={14} className="text-gray-400" />
                  {job.start_date?.slice(0, 10)}
                </div>
              )}
            </div>
          </div>

          {canMatch.includes(user?.role) && (
            <button onClick={() => matchMutation.mutate()} disabled={matchMutation.isPending}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex-shrink-0">
              <Sparkles size={16} />
              {matchMutation.isPending ? 'Matching...' : 'Find Candidates'}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {tabs.map(t => (
          <button key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition
              ${tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {tab === 'overview' && (
        <div className="grid grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Briefcase size={16} className="text-amber-600" />
              Job Details
            </h3>
            <InfoRow label="Worker Category"  value={job.worker_category?.replace('_', ' ')} />
            <InfoRow label="Employment Type"  value={job.employment_type?.replace('_', ' ')} />
            <InfoRow label="Department"       value={job.department} />
            <InfoRow label="Reporting To"      value={job.reporting_to} />
            <InfoRow label="Shift"             value={job.shift} />
            <InfoRow label="Working Hours"     value={job.working_hours ? `${job.working_hours} hrs/day` : null} />
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Users size={16} className="text-blue-600" />
              Requirements
            </h3>
            <InfoRow label="Min Experience"   value={job.min_experience ? `${job.min_experience} yrs` : null} />
            <InfoRow label="Max Experience"   value={job.max_experience ? `${job.max_experience} yrs` : null} />
            <InfoRow label="Min Education"    value={job.min_education} />
            <InfoRow label="Age Range"        value={job.age_min && job.age_max ? `${job.age_min} - ${job.age_max} yrs` : null} />
            <InfoRow label="Gender Preference" value={job.gender_preference} />
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Calendar size={16} className="text-purple-600" />
              Rates & Timeline
            </h3>
            <InfoRow label="Pay Rate"   value={job.pay_rate ? `₹${Number(job.pay_rate).toLocaleString('en-IN')}` : null} />
            <InfoRow label="Bill Rate"  value={job.bill_rate ? `₹${Number(job.bill_rate).toLocaleString('en-IN')}` : null} />
            <InfoRow label="Start Date" value={job.start_date?.slice(0, 10)} />
            <InfoRow label="End Date"   value={job.end_date?.slice(0, 10)} />
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Briefcase size={16} className="text-green-600" />
              Assignment
            </h3>
            <InfoRow label="Assigned To" value={job.users_job_requisitions_assigned_toTousers?.name} />
            <InfoRow label="Client Contact" value={job.clients?.client_contacts?.find(c => c.is_primary)?.name} />
            <InfoRow label="Description" value={job.job_description} />
          </div>
        </div>
      )}

      {/* ── APPLICATIONS TAB ── */}
      {tab === 'applications' && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Users size={16} className="text-blue-600" />
            Applications
          </h3>
          {job.applications?.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">No applications yet</div>
          ) : (
            <div className="space-y-3">
              {job.applications?.map(app => (
                <div key={app.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-600 text-xs font-semibold">
                        {app.candidates?.first_name?.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {app.candidates?.first_name} {app.candidates?.last_name}
                      </p>
                      <p className="text-xs text-gray-400">
                        {app.candidates?.phone} · Profile {app.candidates?.profile_score}%
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {app.stage === 'selected'  && <CheckCircle size={14} className="text-green-600" />}
                    {app.stage === 'rejected'  && <XCircle size={14} className="text-red-600" />}
                    {!['selected','rejected'].includes(app.stage) && <Clock size={14} className="text-amber-600" />}
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${stageColor(app.stage)}`}>
                      {app.stage}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── SMART MATCHES TAB ── */}
      {tab === 'matches' && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Sparkles size={16} className="text-blue-600" />
            Matching Candidates
          </h3>
          {!matchMutation.data ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              <Search size={28} className="mx-auto text-gray-200 mb-3" />
              Click "Find Candidates" to run smart matching for this requisition
            </div>
          ) : matchMutation.data.data.data.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">No matching candidates found</div>
          ) : (
            <div className="space-y-3">
              {matchMutation.data.data.data.map(c => (
                <div key={c.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-600 text-xs font-semibold">{c.first_name?.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{c.first_name} {c.last_name}</p>
                      <p className="text-xs text-gray-400">{c.phone} · {c.total_experience || 0} yrs exp</p>
                    </div>
                  </div>
                  <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-blue-50 text-blue-600">
                    {c.match_score}% match
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default JobDetail;
